import { Flex, Text, Box, IconButton } from "@chakra-ui/react";
import { RModal } from "components";
import CheckedTag from "components/CheckedTag/CheckedTag";
import RaceLevel from "components/Races2/RaceLevel/RaceLevel";
import { IconDoubleArrow, IconEmptySet, IconInfoGradient, IconStatsUpgrade } from "icons";
import { useState, useCallback } from "react";

// Define a type for stats
type StatType = {
  name: string;
  icon?: React.ReactNode;
};

// Define a type for carousel slides
interface SlideData {
  level: number;
  stats: StatType[];
}

interface SuggestedStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  slides?: SlideData[];
}

// Sample data structure for slides
const defaultSlides: SlideData[] = [
  {
    level: 1,
    stats: [
      { name: "Speed" },
      { name: "Fast Turns" },
    ],
  },
  {
    level: 2,
    stats: [
      { name: "Speed" },
      { name: "Fast Turns" },
    ],
  },
  {
    level: 3,
    stats: [
      { name: "Speed" },
      { name: "Fast Turns" },
      { name: "Handling" },
    ],
  },
  {
    level: 4,
    stats: [
      { name: "Speed" },
      { name: "Fast Turns" },
      { name: "Handling" },
      { name: "Slow Turns" },
    ],
  },
  {
    level: 5,
    stats: [
      { name: "Speed" },
      { name: "Fast Turns" },
      { name: "Handling" },
      { name: "Slow Turns" },
      { name: "Cooling" },
      { name: "Braking" },
    ],
  },
  {
    level: 6,
    stats: [
      { name: "Speed" },
      { name: "Fast Turns" },
      { name: "Handling" },
      { name: "Slow Turns" },
      { name: "Cooling" },
      { name: "Braking" },
      { name: "Acceleration" },
      { name: "Stability" },
    ],
  },
];

const SuggestedStatsModal = ({ 
  isOpen, 
  onClose,
  slides = defaultSlides 
}: SuggestedStatsModalProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const handlePrevious = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [slides.length]);

  const handleNext = useCallback(() => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  }, [slides.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  return (
    <RModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      title="Suggested Stats"
      icon={<IconInfoGradient />}
    >
      <Flex
        flexDir="column"
        gap="4"
      >
        <Text
          fontSize={{ base: '0.75rem', md: '0.875rem'}}
          lineHeight={{ base: '0.875rem', md: '1.125rem'}}
          fontWeight={500}
          color="white.80"
        >
          The track&apos;s layout and projected forecast determine Suggested Stats—select cars that best align with these stats.
        </Text>

        {/* Carousel slide */}
        <Flex
          position="relative"
          w="full"
          borderRadius="0.5rem"
          background="linear-gradient(90deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%)"
          p="1rem"
          _before={{
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(79, 197, 186, 0.6) 0%, rgba(79, 197, 186, 0.06) 100%)',
            borderRadius: '0.5rem',
            padding: '2px',
            mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
            maskComposite: "exclude",
            zIndex: -1,
          }}
        >
          <Flex
            flexDir="column"
            gap="0.5rem"
            w="full"
          >
            <Flex>
              <RaceLevel
                level={slides[currentSlide].level}
              />
            </Flex>

            <Flex
              background="linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 100%)"
              p="0.5rem"
              w="full"
              borderRadius="0.25rem"
              flexDir="column"
              gap="3"
            >
              <Flex alignItems="center" gap="0.5rem" pl="0.25rem">
                <IconStatsUpgrade 
                  __css={{
                    path: {
                      fill: 'white.100',
                    }
                  }}
                />

                <Text
                  fontSize={{ base: '0.875rem', md: '1.125rem'}}
                  lineHeight={{ base: '1.125rem', md: '1.5rem'}}
                  fontWeight={600}
                  color="white.80"
                >
                  Stats
                </Text>
              </Flex>

              <Flex pl="0.25rem" flexWrap="wrap" gap="2">
                {slides[currentSlide].stats.map((stat, index) => (
                  <CheckedTag title={stat.name} key={`stat-${index}`} />
                ))}
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        {/* Carousel pagination and navigation */}
        <Flex justify="center" align="center" mt="2" gap="2">
          {/* Previous button */}
          <IconButton
            aria-label="Previous slide"
            icon={<IconDoubleArrow transform="rotate(180deg)" />}
            onClick={handlePrevious}
            variant="unstyled"
            display="flex"
            isDisabled={currentSlide === 0}
            alignItems="center"
            justifyContent="center"
            background="linear-gradient(225deg, rgba(240, 240, 240, 0.1) 0%, rgba(197, 197, 197, 0.1) 18%, rgba(176, 176, 176, 0.1) 73%, rgba(126, 126, 126, 0.1) 100%)"
            p="3"
            position="relative"
            _hover={{
              background: 'rgba(240, 240, 240, 0.2)',
            }}
            transition="background 0.3s ease"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(360deg, rgba(240, 240, 240, 0.3) 0%, rgba(240, 240, 240, 0.12) 100%)',
              padding: '2px',
              borderRadius: '0.25rem',
              mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              maskComposite: "exclude",
            }}
            minW="auto"
            h="auto"
          />

          {/* Pagination dots */}
          <Flex gap="2">
            {slides.map((_, index) => (
              <Box
                key={`dot-${index}`}
                w="0.75rem"
                h="0.75rem"
                borderRadius="full"
                bg={currentSlide === index ? "white" : "rgba(255, 255, 255, 0.3)"}
                cursor="pointer"
                onClick={() => goToSlide(index)}
              />
            ))}
          </Flex>

          {/* Next button */}
          <IconButton
            aria-label="Next slide"
            icon={<IconDoubleArrow />}
            onClick={handleNext}
            variant="unstyled"
            display="flex"
            isDisabled={currentSlide === slides.length - 1}
            alignItems="center"
            justifyContent="center"
            background="linear-gradient(225deg, rgba(240, 240, 240, 0.1) 0%, rgba(197, 197, 197, 0.1) 18%, rgba(176, 176, 176, 0.1) 73%, rgba(126, 126, 126, 0.1) 100%)"
            p="3"
            position="relative"
            minW="auto"
            h="auto"
            _hover={{
              background: 'rgba(240, 240, 240, 0.2)',
            }}
            transition="background 0.3s ease"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(360deg, rgba(240, 240, 240, 0.3) 0%, rgba(240, 240, 240, 0.12) 100%)',
              padding: '2px',
              borderRadius: '0.25rem',
              mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
              maskComposite: "exclude",
            }}
          />
        </Flex>
      </Flex>
    </RModal>
  );
};

export default SuggestedStatsModal;